import React, { useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Stack, Typography, useTheme,
} from '@mui/material';
import {
  GetParticipantsResponse, Partner, TripHistories, TripMatch,
} from '@marageti/z4-sdk/lib/travel';
import { useParams } from 'react-router-dom';
import { useApiClient, useFetch, useFetchImmutable } from '@marageti/z4-lib';
import ZSpinner from '../../../components/z-lib/z-spinner';
import ReviewCard from '../cards/review-card';
import AboutCard from '../cards/about-card';
import TeamCard from '../cards/team-card';
import ProfileCard from '../cards/profiles-card';
import ParticipantsCard from '../cards/participants-card';
import { useAuthStore } from '../../../store';
import InviteCoTravelerView from '../../trip-request/invite';

interface SidebarProps {
  isMessagingPage?: boolean;
  isLegacyTR?: boolean;
  isAboutPage?: boolean;
  partner: Partner | undefined;
}

const Sidebar = ({
  isMessagingPage = false,
  isLegacyTR = false,
  isAboutPage = false,
  partner,
}: SidebarProps) => {
  const { tripMatchId } = useParams();
  const theme = useTheme();
  const apiClient = useApiClient();
  const { loggedInTraveler } = useAuthStore();
  const [showInviteCoTravelers, setShowInviteCoTravelers] = useState(false);
  const { data: tripMatch } = useFetch<TripMatch>(apiClient.tripMatchesClient.getTripMatch, [tripMatchId], !!tripMatchId);
  const { data: participants, mutate: mutateParticipants } = useFetch<GetParticipantsResponse>(
    apiClient.conversationsClient.getParticipants,
    [tripMatch?.travelerToPartnerId, { params: { inactive: false } }],
    !!tripMatch?.travelerToPartnerId,
  );
  const { data: tripHistories } = useFetchImmutable<TripHistories>(apiClient.peopleClient.getTravelerTripHistories, [loggedInTraveler?.id], !!loggedInTraveler?.id);
  const canInviteCoTravelers = useMemo(() => {
    if (!tripMatch || !tripHistories) return false;
    return tripHistories.activeTrips?.some((trip) => trip.id === tripMatch.tripRequestId);
  }, [tripMatch, tripHistories]);

  return (
    <Box
      sx={{
        position: 'sticky',
        top: () => {
          const toolbarHeight = theme.mixins.toolbar.minHeight;
          return `calc(${toolbarHeight}px + ${theme.spacing(6)})`;
        },
        pr: 3,
        pb: { desktop: 0 },
        height: 'calc(100vh - 186px)',
        overflow: 'auto',
      }}
    >
      <Stack spacing={2.5}>
        {isMessagingPage ? (
          <>
            {isLegacyTR
            && (
              <Alert
                severity="warning"
                sx={{
                  p: 3, backgroundColor: 'warning.light',
                }}
                variant="outlined"
              >
                <AlertTitle>Prior Communication</AlertTitle>
                Zicasso has moved to a new messaging platform. Previous email communications are not included in this chat.
              </Alert>
            )}
            <Box>
              <Typography variant="h4" sx={{ mb: 1.5 }}>You Are Talking To</Typography>
              <ParticipantsCard
                tripMatch={tripMatch}
                loggedInTraveler={loggedInTraveler}
                participants={participants}
                canInviteCoTravelers={canInviteCoTravelers}
                onInviteCoTravelers={() => setShowInviteCoTravelers(true)}
              />
            </Box>
          </>
        ) : (
          <>
            <Typography variant="h4" sx={{ mb: 1.5 }}>Your Team</Typography>
            <TeamCard assignedAgentId={tripMatch?.assignedTo} participants={participants} />
          </>
        )}
        <ProfileCard partner={partner} hideWebsite />
        {(partner?.aboutUs && !isAboutPage) && (
        <AboutCard aboutUs={partner.aboutUs} onSidebar />
        )}
        {!isAboutPage && (
          <Box>
            {partner?.id ? (
              <ReviewCard partnerId={partner?.id} />
            ) : (
              <Box display="flex" justifyContent="center">
                <ZSpinner />
              </Box>
            )}
          </Box>
        )}
      </Stack>
      {showInviteCoTravelers && (
        <InviteCoTravelerView
          tripRequestId={tripMatch?.tripRequestId}
          onClose={() => setShowInviteCoTravelers(false)}
          onSuccess={mutateParticipants}
        />
      )}
    </Box>
  );
};

export default Sidebar;
